import { Text, Flex, Button } from "@chakra-ui/react"
import { useNavigate } from 'react-router-dom'
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type ModalFormData } from '../../schemas'
import { InputField } from "@/components/InputField"
import { FunctionComponent } from "react"
import { ChakraFeatherIcon } from "@/components/ChakraFeatherIcon"
import { ChallengeStatus } from "@/model"

type Step3FieldsProps = {
  onStepSuccess?: VoidFunction
}

export const Step3Fields: FunctionComponent<Step3FieldsProps> = ({ onStepSuccess }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const form = useFormContext<ModalFormData>()

  const handleCancel = () => {
    navigate('/application/challenge')
  }

  const handleNext = async () => {
    const isValid = await form.trigger()

    if (isValid) {
      onStepSuccess?.()
    }
  }

  return (
    <Flex direction="column" gap={8} maxW={{ base: '100%', md: '50%', lg: '30%' }}>
      <Text fontSize="16px" fontWeight="medium">{t('pages.challenges_details.tabs.tab_3.title')}</Text>

      <InputField
        rootProps={{
          isInvalid: !!form.formState.errors?.error_message,
          errorMessage: form.formState.errors?.error_message?.message,
          sx: {
            input: {
              marginTop: { base: 9 }
            }
          }
        }}
        label={
          <Flex direction="column">
            <Text>{t('pages.challenges_details.tabs.tab_3.input_incorrect_label')}</Text>
            <Text fontSize="14px" fontWeight="normal" color="gray.500">({t('pages.challenges_details.tabs.tab_3.input_incorrect_helptext')})</Text>
          </Flex>
        }
        maxLength={400}
        helperText={`(${t('utils.components.input.max_chars', { chars: 400 })})`}
        {...form.register('error_message')}
      />

      <InputField
        rootProps={{
          isInvalid: !!form.formState.errors?.timeout_message,
          errorMessage: form.formState.errors?.timeout_message?.message,
          sx: {
            input: {
              marginTop: { base: 9 }
            }
          }
        }}
        label={
          <Flex direction="column">
            <Text>{t('pages.challenges_details.tabs.tab_3.input_time_label')}</Text>
            <Text fontSize="14px" fontWeight="normal" color="gray.500">({t('pages.challenges_details.tabs.tab_3.input_time_helptext')})</Text>
          </Flex>
        }
        maxLength={400}
        helperText={`(${t('utils.components.input.max_chars', { chars: 400 })})`}
        {...form.register('timeout_message')}
      />

      <InputField
        rootProps={{
          isInvalid: !!form.formState.errors?.communication_message,
          errorMessage: form.formState.errors?.communication_message?.message,
          sx: {
            input: {
              marginTop: { base: 9 }
            }
          }
        }}
        label={
          <Flex direction="column">
            <Text>{t('pages.challenges_details.tabs.tab_3.input_other_winner_label')}</Text>
            <Text fontSize="14px" fontWeight="normal" color="gray.500">({t('pages.challenges_details.tabs.tab_3.input_other_winner_helptext')})</Text>
          </Flex>
        }
        maxLength={400}
        helperText={`(${t('utils.components.input.max_chars', { chars: 400 })})`}
        {...form.register('communication_message')}
      />

      <InputField
        rootProps={{
          isInvalid: !!form.formState.errors?.winner_message,
          errorMessage: form.formState.errors?.winner_message?.message,
          sx: {
            input: {
              marginTop: { base: 9 }
            }
          }
        }}
        label={
          <Flex direction="column">
            <Text>{t('pages.challenges_details.tabs.tab_3.input_winner_label')}</Text>
            <Text fontSize="14px" fontWeight="normal" color="gray.500">({t('pages.challenges_details.tabs.tab_3.input_winner_helptext')})</Text>
          </Flex>
        }
        maxLength={400}
        helperText={`(${t('utils.components.input.max_chars', { chars: 400 })})`}
        {...form.register('winner_message')}
      />

      <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
        <Button
          display='flex'
          alignItems='center'
          gap={2}
          onClick={() => {
            form.setValue('status', ChallengeStatus.DRAFT);
            handleNext?.()
          }}
          width={{ base: '100%', md: 'initial' }}
          p={0}
          color='brand.500'
          backgroundColor='transparent'
          textDecoration='underline'
          _hover={{
            backgroundColor: 'transparent',
            color: 'brand.500',
          }}
        >
          <ChakraFeatherIcon name='save' />
          {t('utils.components.button.save_as_draft')}
        </Button>

        <Flex w={'100%'} justifyContent='end' gap={4}>    
          <Button variant='outlined' color='gray.600' onClick={handleCancel}>
            {t('utils.components.button.cancel')}
          </Button>
          <Button
            bg="green.500"
            _hover={{
              bg: 'green.600'
            }}
            onClick={() => {
              form.setValue('status', ChallengeStatus.PUBLISHED);
              handleNext?.();
            }}
            isLoading={form.formState.isValidating}
          >
            {t('utils.components.button.create')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}